import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import type {
  evereeEmbeddedSession,
  evereeEmbeddedSessionData,
} from "types/api-types/payroll/everee-embedded-sessions/everee-embedded-sessions.types";

type EvereeEmbedType = NonNullable<evereeEmbeddedSessionData["experience"]>;

/**
 * EvereeViewStore - Manages Everee payroll embed session state
 *
 * This is a plain ViewStore class (not extending ViewStore base class)
 * following project patterns for route-level stores.
 */
class EvereeViewStore {
  rootStore: RootStore;

  // Observable Properties
  embedType: EvereeEmbedType = "WORKER_HOME";
  sessionUrl: string | null = null;
  sessionOrigin: string | null = null;
  isSessionLoading = false;
  sessionError: string | null = null;
  isHandshakeComplete = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessor
  get evereeEmbeddedSessionsStore(): ServiceStore {
    return this.rootStore.evereeEmbeddedSessionsStore as ServiceStore;
  }

  // Synchronous Actions
  setEmbedType = (type: EvereeEmbedType) => {
    this.embedType = type;
  };

  setHandshakeComplete = (complete: boolean) => {
    this.isHandshakeComplete = complete;
  };

  reset = () => {
    this.sessionUrl = null;
    this.sessionOrigin = null;
    this.sessionError = null;
    this.isHandshakeComplete = false;
  };

  // Async Actions
  requestSession = async (experience: EvereeEmbedType) => {
    this.isSessionLoading = true;
    this.sessionError = null;

    try {
      const currentUser = this.rootStore.authStore?.currentUser as unknown as Record<string, unknown> | undefined;
      const session = (await this.evereeEmbeddedSessionsStore.create({
        experience,
        workerId: (currentUser?.evereeWorkerId as string) || String(currentUser?.id),
      })) as unknown as evereeEmbeddedSession;

      runInAction(() => {
        this.sessionUrl = session.url;
        this.sessionOrigin = session.origin;
        this.embedType = experience;
      });
    } catch (error) {
      runInAction(() => {
        this.sessionError =
          error instanceof Error
            ? error.message
            : "Failed to create Everee session";
      });
    } finally {
      runInAction(() => {
        this.isSessionLoading = false;
      });
    }
  };

  // Handle Everee message events from iframe
  handleEvereeMessage = (eventType: string, data: unknown) => {
    switch (eventType) {
      case "MESSAGE_PORT_REGISTERED":
        this.handlePortRegistered();
        break;
      case "WORKER_ONBOARDING_COMPLETE":
        this.handleOnboardingComplete();
        break;
      case "DISMISS":
        this.handleDismiss();
        break;
      case "EMB-101": // Session creation token expired
        this.handleTokenExpiry();
        break;
      case "EMB-102": // No event handler registered
        this.handleNoEventHandler(data);
        break;
      case "EMB-201": // Onboarding already complete
        this.handleAlreadyOnboarded();
        break;
      case "EMB-202": // Onboarding not yet complete
        this.handleOnboardingNotComplete();
        break;
      default:
        console.log("[Everee] Unhandled event:", eventType, data);
    }
  };

  private handlePortRegistered = () => {
    // Message port successfully registered with the Component
    console.log("[Everee] Message port registered successfully");
  };

  private handleOnboardingComplete = () => {
    // Refresh user profile to get updated payroll status
    // Navigate to worker home
    this.rootStore.routerStore?.goTo("Payroll Home");
  };

  private handleDismiss = () => {
    // Component is requesting dismissal - navigate back or to home
    this.rootStore.routerStore?.goTo("Payroll Home");
  };

  private handleTokenExpiry = async () => {
    // Auto-refresh session without user intervention
    await this.requestSession(this.embedType);
  };

  private handleNoEventHandler = (data: unknown) => {
    // No event handler registered - log for debugging
    console.error("[Everee] No event handler registered:", data);
  };

  private handleAlreadyOnboarded = () => {
    // Redirect to worker home
    this.rootStore.routerStore?.goTo("Payroll Home");
  };

  private handleOnboardingNotComplete = () => {
    // Only ONBOARDING experience available until onboarding is complete
    // Redirect to onboarding
    this.rootStore.routerStore?.goTo("Employee Onboarding");
  };

  // Computed Values
  get isLoading() {
    return this.isSessionLoading || this.evereeEmbeddedSessionsStore?.isServicePending;
  }

  get hasSession() {
    return this.sessionUrl !== null;
  }
}

export default EvereeViewStore;
